import moment from 'moment';
import type { FC } from 'react';
import { useMemo } from 'react';

import { theme, Timeline } from '@catalyze/ui';
import type { ITimelineRecord } from 'api/types/lead';
import { Status } from 'api/types/lead';
import { leadStatusColor } from 'utils/leadGroupColor';

import { TimelineWrapper, TimelineTitle } from './styled';

interface IRecordProps {
    title: string;
    description: string;
    color: string;
    created: string;
    text: string | undefined;
}

interface ITimelineContainer {
    timelineData: ITimelineRecord[];
}

const recordFactory = (steps: ITimelineRecord[]): IRecordProps[] => {
    return steps.map((step) => {
        let title = '';
        let text;
        switch (step.status) {
            case Status.NEW:
            case Status.RECOMMENDED:
                title = 'Recommended';
                break;
            case Status.VIEWED:
                title = 'Viewed';
                break;
            case Status.CONTACTED:
                title = 'Contacted';
                if (step.description) {
                    if (
                        parseInt(step.description, 10) &&
                        parseInt(step.description, 10).toString().length === 10
                    ) {
                        text = `
                            (${step.description.slice(0, 3)})
                            ${step.description.slice(3, 6)}
                            - ${step.description.slice(6)} on
                        `;
                    } else {
                        text = `${step.description} on`;
                    }
                }
                break;
            case Status.ACTIVE:
                title = 'Marked as active';
                break;
            case Status.LISTED:
                title = 'Marked as listed';
                break;
            case Status.CLOSED:
                title = 'Closed';
                break;
            case Status.SOLD:
                title = 'Sold';
                break;
            case Status.ARCHIVED:
                title = 'Archived';
                break;
            case Status.DELETED:
                title = 'Deleted';
                break;
            default:
                break;
        }

        const description = new Date(step.created).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });

        return {
            color: leadStatusColor({ theme, status: step.status }),
            title,
            description,
            text,
            created: step.created,
        };
    });
};

export const TimelineContainer: FC<ITimelineContainer> = ({ timelineData }) => {
    const sortedTimelineData: ITimelineRecord[] = useMemo(() => {
        // eslint-disable-next-line id-length
        return timelineData.sort((a: ITimelineRecord, b: ITimelineRecord) =>
            moment(a.created).diff(moment(b.created))
        );
    }, [timelineData]);
    const timelineSteps = recordFactory(sortedTimelineData);
    return (
        <TimelineWrapper>
            <TimelineTitle>Timeline</TimelineTitle>
            <Timeline steps={timelineSteps} />
        </TimelineWrapper>
    );
};
